import type { DeviceSchedule } from '../lib/types';

/**
 * One schedule slot's display card. Mirrors iOS
 * `DeviceScheduleItemView`:
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │ SCHEDULE 1                       [● Enabled]     │
 *   │ ─────────────────────────────────────────────    │
 *   │ ┌──────────┐    ┌──────────┐                     │
 *   │ │ ON       │    │ OFF      │      [S2]           │
 *   │ │ 06:00    │    │ 22:00    │                     │
 *   │ └──────────┘    └──────────┘                     │
 *   └──────────────────────────────────────────────────┘
 *
 * Time chips are big and tabular-numeric so the schedule reads at a
 * glance. Disabled slots are shown muted with a single "Disabled"
 * label instead of the time chips, matching the iOS pattern of
 * keeping the slot visible but inactive.
 */
export function ScheduleCard({
  schedule,
  index,
}: {
  schedule: DeviceSchedule;
  index: number;
}) {
  const isEnabled = schedule.isEnabled === 1;
  const start = formatTime(
    schedule.scheduleTime?.startTime?.hours,
    schedule.scheduleTime?.startTime?.minutes,
  );
  const stop = formatTime(
    schedule.scheduleTime?.stopTime?.hours,
    schedule.scheduleTime?.stopTime?.minutes,
  );

  return (
    <div
      style={{
        background: 'var(--aqua-surface)',
        border: '1px solid var(--aqua-border)',
        borderRadius: 12,
        padding: '12px 14px',
        boxShadow: 'var(--aqua-shadow-sm)',
        opacity: isEnabled ? 1 : 0.85,
      }}
    >
      {/* Header — caps, tracked, brand color (iOS pattern) */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span
          style={{
            color: 'var(--aqua-primary)',
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 1,
          }}
        >
          SCHEDULE {index + 1}
        </span>
        <span
          className="d-inline-flex align-items-center gap-1"
          style={{
            color: isEnabled ? 'var(--aqua-online)' : 'var(--aqua-text-muted)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.3,
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: isEnabled
                ? 'var(--aqua-online)'
                : 'var(--aqua-text-muted)',
            }}
            aria-hidden="true"
          />
          {isEnabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      <div
        style={{
          height: 1,
          background: 'var(--aqua-border)',
          marginBottom: 12,
        }}
      />

      {/* Body — time chips + speed pill (or muted "Disabled" message) */}
      <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
        {isEnabled ? (
          <>
            <div className="d-flex gap-2">
              <TimeChip label="ON" time={start} variant="on" />
              <TimeChip label="OFF" time={stop} variant="off" />
            </div>
            <SpeedPill slot={schedule.SchSpeedSelect || 'S1'} />
          </>
        ) : (
          <div
            className="text-muted"
            style={{ fontSize: 13, fontStyle: 'italic' }}
          >
            This schedule is currently off.
          </div>
        )}
      </div>

      {schedule.scheduleName &&
        schedule.scheduleName !== `Schedule ${index + 1}` && (
          <div
            className="mt-2 small text-muted"
            style={{ fontStyle: 'italic' }}
          >
            {schedule.scheduleName}
          </div>
        )}
    </div>
  );
}

function TimeChip({
  label,
  time,
  variant,
}: {
  label: string;
  time: string;
  variant: 'on' | 'off';
}) {
  const accent = variant === 'on' ? 'var(--aqua-online)' : '#9aa0a6';
  return (
    <div
      style={{
        background: 'var(--aqua-surface-muted)',
        border: '1px solid var(--aqua-border)',
        borderRadius: 8,
        padding: '6px 12px',
        minWidth: 76,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          color: accent,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 1,
          lineHeight: 1,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: 'var(--aqua-text-strong)',
          fontSize: 18,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 2,
          lineHeight: 1.1,
        }}
      >
        {time}
      </div>
    </div>
  );
}

function SpeedPill({ slot }: { slot: string }) {
  return (
    <span
      style={{
        backgroundColor: 'rgba(1, 157, 220, 0.15)',
        color: 'var(--aqua-primary)',
        fontWeight: 700,
        fontSize: 13,
        padding: '0.4rem 0.8rem',
        borderRadius: 999,
        letterSpacing: 0.5,
      }}
    >
      {slot}
    </span>
  );
}

/**
 * Render a schedule time in 12-hour AM/PM form (e.g. "11:50 AM",
 * "9:00 PM"). The pump stores hours/minutes as 24-hour strings; we
 * convert here for display only — the editor (`<input type="time">`)
 * and the wire format both stay 24-hour. Locale forced to en-US so
 * the format is consistent across all browsers; AquaStar is a US
 * product. If/when we localise more aggressively we can switch to
 * `getUserLocale()` and respect the browser preference.
 */
function formatTime(h?: string, m?: string): string {
  const hh = Number(h ?? 0);
  const mm = Number(m ?? 0);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
    return `${String(h ?? '0').padStart(2, '0')}:${String(m ?? '0').padStart(2, '0')}`;
  }
  // Anchor date is irrelevant — we only format hour/minute.
  const d = new Date(2000, 0, 1, hh, mm);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}
